import { MouseEvent, useCallback, useState } from 'react';
import { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { isHaveChildren } from 'PublishRulesPackage/MainModule/utils/isHaveChildren';
import { IconCollapse } from 'UIPackage/IconsModule/components/IconCollapse';
import { IconSettings } from 'UIPackage/IconsModule/components/IconSettings';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { setPublishRulesActiveTreeItem } from 'PublishRulesPackage/MainModule/actions/publishRulesActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './PublishRulesTreeItem.module.scss';
import { getPublishRulesActiveTreeItem } from '../selectors/publishRulesSelectors';
import { IPublishRulesTreeItem } from '../interfaces/IPublishRulesTreeItem';

interface IPublishRulesTreeItemProps {
  item: IPublishRulesTreeItem;
  level: number;
  isParentExpansible?: boolean;
  activeTreeItem: IPublishRulesTreeItem;
}

const PublishRulesTreeItemWithoutStore = ({
  item,
  level,
  activeTreeItem,
  isParentExpansible,
}: IPublishRulesTreeItemProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isOpen, setIsOpen] = useState(!!item.defaultExpanded);

  const isActive = item.id === activeTreeItem.id;

  const toggleAccordion = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    },
    [isOpen],
  );

  const handleClick = useCallback(
    (newActiveItem: IPublishRulesTreeItem) => {
      item.editable && dispatch(setPublishRulesActiveTreeItem(newActiveItem));
    },
    [item.editable],
  );

  const renderChildrenItems = (
    childrenItems: IPublishRulesTreeItem[],
    currentLevel: number,
    parentExpansible?: boolean,
  ) => {
    return childrenItems.map((children, index) => (
      <PublishRulesTreeItem
        key={`publish-rules-tree-item-${currentLevel}-${index}`}
        item={children}
        level={currentLevel + 1}
        isParentExpansible={parentExpansible}
      />
    ));
  };

  const titleWrapperClassNames = cn([
    styles.TitleWrapper,
    isActive ? styles.TitleWrapperActive : '',
    !item.editable ? styles.TitleWrapperUnavailable : '',
  ]);
  const iconCollapseClassNames = cn([
    styles.CollapseIcon,
    isActive ? styles.CollapseIconActive : '',
    isOpen ? styles.CollapseIconOpen : '',
  ]);

  const calcPadding = level * 16 + (isParentExpansible ? 16 : 0);

  return (
    <div className={styles.Wrapper}>
      <div
        className={titleWrapperClassNames}
        onClick={() => !isActive && handleClick(item)}
        style={{ paddingLeft: calcPadding }}
      >
        {item.expansible && (
          <div onClick={e => (item.expansible ? toggleAccordion(e) : null)}>
            <IconCollapse className={iconCollapseClassNames} />
          </div>
        )}
        <span>{item.title}</span>
        {item.haveSavedSettings && <IconSettings className={styles.SettingsIcon} />}
      </div>

      {isHaveChildren(item?.children) && isOpen && (
        <div className={styles.ChildrenWrapper}>{renderChildrenItems(item?.children, level, item.expansible)}</div>
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  activeTreeItem: getPublishRulesActiveTreeItem(state),
});

export const PublishRulesTreeItem = connect(mapStateToProps)(PublishRulesTreeItemWithoutStore);
